import { Measure } from '@cbsi-cbscom/measure';
import { AssetTypePM } from 'library/ts/model/enums/metrics/performance/AssetTypePM';
import { UserTypePM } from 'library/ts/model/enums/metrics/performance/UserTypePM';
import { UserType } from 'library/ts/model/user/UserType';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { IPlayerAdapterEventVO } from 'library/ts/player/adapter/interfaces/events/IPlayerAdapterEventVO';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Metrics } from 'library/ts/utilities/measure/Metrics';
import { Utilities } from 'library/ts/utilities/Utilities';
import { INewRelicManager } from 'tag/ts/smart_tag/interfaces/utilities/INewRelicManager';
import { IPerformanceMetricsManager } from 'tag/ts/smart_tag/interfaces/utilities/IPerformanceMetricsManager';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { IParamsContent } from 'tag/ts/tag/interfaces/params/IParamsContent';
import { NewRelicManager } from 'tag/ts/tag/metrics/NewRelicManager';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';

interface WindowWithLibraries {
	Hls?: { version?: string }
	shaka?: { Player?: { version?: string } }
	utag?: { cfg?: { v?: string } }
}

/**
 * @copyright © Paramount Streaming
 * @author Todd Alexander
 * @description New Relic performance metrics manager
 */
export class PerformanceMetricsManager implements IPerformanceMetricsManager {
	private static _instance: IPerformanceMetricsManager = new PerformanceMetricsManager();

	#logger: ILogger;
	#newRelic: INewRelicManager;
	#tag: ITag;

	constructor() {
		this.#logger = LogService.create( { prefixes: [ 'SmartTag:', this.name + ':' ] } );
		this.#newRelic = NewRelicManager.getInstance();
	}

	/**
	 * @description Name of this class
	 */
	get name(): string {
		return 'PerformanceMetricsManager';
	}

	public static getInstance() {
		return PerformanceMetricsManager._instance;
	}

	/**
	 * @description Reference to global Utilities instance
	 * @return {Utilities | undefined}
	 */
	get utils(): Utilities | undefined {
		return window?.PARA?.PlayerServices?.Utilities;
	}

	/**
	 * @description Returns Smart Tag application name
	 * @return {string}
	 */
	#appName(): string {
		return 'SmartTag';
	}

	/**
	 * @description Returns Smart Tag application version from global project info
	 * @return {string}
	 */
	#appVersion(): string {
		return window?.SmartTag?.info?.version
			|| window?.PlayerServicesProjectInfo?.release_version
			|| 'unknown';
	}

	/**
	 * @description Returns Avia-js player version parsed from player URL in project info
	 * Handles various version formats: 2.54.0, v2.54.0, 2.54.0.1
	 * @return {string}
	 */
	#aviaVersion(): string {
		try {
			// Extract version from player URL: https://avia.vtg.paramount.tech/avia-js/2.54.0/player/avia.min.js
			const playerUrl = window?.PlayerServicesProjectInfo?.dependencies?.player?.url?.regular?.value;
			if ( this.utils?.Type?.isString( playerUrl ) && playerUrl.length > 0 ) {
				// Flexible regex: optional 'v' prefix, 3 or 4-part semver
				const match = playerUrl.match( /avia-js\/v?(\d+\.\d+\.\d+(?:\.\d+)?)\// );
				if ( Array.isArray( match ) && match.length > 1 && this.utils?.Type?.isString( match[ 1 ] ) ) {
					return match[ 1 ];
				}
			}
		} catch ( error ) {
			// Silently fail - version extraction should never break metrics
		}
		return 'unknown';
	}

	/**
	 * @description Checks if EmbedType is HTML5
	 * @return {boolean}
	 */
	#isHTML5(): boolean {
		return this.#tag?.params?.SYSTEM?.embedType === EmbedType.HTML5;
	}

	/**
	 * @description For performance logging purposes, returns SmartTag EmbedType
	 * @return {EmbedType}
	 */
	#embedType(): EmbedType {
		if ( this.#isHTML5() === true ) {
			return EmbedType.HTML5;
		} else {
			return EmbedType.DESKTOP;
		}
	}

	/**
	 * @description Returns the embed platform from SYSTEM params
	 * @return {EmbedPlatform}
	 */
	#platform() {
		return this.#tag?.params?.SYSTEM?.platform;
	}

	/**
	 * @description Returns the embed device from SYSTEM params
	 * @return {EmbedDevice}
	 */
	#device() {
		return this.#tag?.params?.SYSTEM?.device;
	}

	/**
	 * @description Returns the device version from SYSTEM params
	 * @return {string}
	 */
	#deviceVersion() {
		return this.#tag?.params?.SYSTEM?.deviceVersion;
	}

	/**
	 * @description Returns HLS.js library version if available at runtime.
	 * Aligns with CTV team's 'vHls' attribute (from v_hls).
	 * @return {string | undefined}
	 */
	#hlsVersion(): string | undefined {
		try {
			const hlsVersion = ( window as unknown as WindowWithLibraries )?.Hls?.version;
			if ( this.utils?.Type?.isString( hlsVersion ) && hlsVersion.length > 0 ) {
				return hlsVersion;
			}
		} catch ( error ) {
			// Silently fail
		}
		return undefined;
	}

	/**
	 * @description Returns Shaka Player library version if available at runtime.
	 * Aligns with CTV team's 'vShaka' attribute (from v_shaka).
	 * @return {string | undefined}
	 */
	#shakaVersion(): string | undefined {
		try {
			const shakaVersion = ( window as unknown as WindowWithLibraries )?.shaka?.Player?.version;
			if ( this.utils?.Type?.isString( shakaVersion ) && shakaVersion.length > 0 ) {
				return shakaVersion;
			}
		} catch ( error ) {
			// Silently fail
		}
		return undefined;
	}

	/**
	 * @description Returns the Tealium utag library version if available at runtime.
	 * Aligns with CTV team's 'vUtag' attribute (from v_utag).
	 * @return {string | undefined}
	 */
	#utagVersion(): string | undefined {
		try {
			const utagVersion = ( window as unknown as WindowWithLibraries )?.utag?.cfg?.v;
			if ( this.utils?.Type?.isString( utagVersion ) && utagVersion.length > 0 ) {
				return utagVersion;
			}
		} catch ( error ) {
			// Silently fail
		}
		return undefined;
	}

	/**
	 * @description Returns the operating system from Capabilities
	 * @return {OperatingSystem}
	 */
	#os() {
		return window?.SmartTag?.capabilities?.os;
	}

	/**
	 * @description Returns the operating system version from Capabilities
	 * @return {string}
	 */
	#osVersion() {
		return window?.SmartTag?.capabilities?.osVersion;
	}

	/**
	 * @description Returns the browser name from Capabilities
	 * @return {string}
	 */
	#browser() {
		return window?.SmartTag?.capabilities?.browser;
	}

	/**
	 * @description Returns the browser major version from Capabilities
	 * @return {number}
	 */
	#browserVersion() {
		return window?.SmartTag?.capabilities?.browserVersion;
	}

	/**
	 * @description Returns the full browser version string from Capabilities
	 * @return {string}
	 */
	#browserVersionFull() {
		return window?.SmartTag?.capabilities?.browserVersionFull;
	}

	/**
	 * @description For performance logging purposes, returns whether assetType is live stream or not
	 * @return {Boolean}
	 * @param {IParamsContent} contentParams
	 */
	#isLive( contentParams: IParamsContent ): boolean {
		if (
			this.utils.Type.isString( contentParams?.assetURL )
			&& this.utils.Type.isEmpty( contentParams?.assetURL ) === false
			&& contentParams?.isLive === true
		) {
			return true;
		} else {
			return contentParams?.isLive ?? false;
		}
	}

	/**
	 * @description For performance logging purposes, returns whether assetType is syncbak stream or not
	 * @return {Boolean}
	 * @param {IParamsContent} contentParams
	 */
	#isSyncbak( contentParams: IParamsContent ): boolean {
		// if live asset url
		if ( this.#isLive( contentParams ) && this.utils.Type.isString( contentParams?.assetURL ) ) {
			// is syncbak?
			return ( this.utils.URL.parser( contentParams?.assetURL ).host.indexOf( '.syncbak.com' ) > -1 );
		}
		// default output
		return false;
	}

	/**
	 * @description For performance logging purposes, returns whether user is Commercial Free or any version of Limited Commercial
	 * @return {UserTypePM}
	 */
	#userType(): UserTypePM {
		let output: UserTypePM = UserTypePM.ANON;
		// user status converted to our internal model + stringified for comparison
		const userTypeString = this.#tag?.tagUtilities.getUserType( this.#tag?.params?.USER?.userStatus );

		switch ( userTypeString ) {
			case UserType.CF:
				output = UserTypePM.CF;
				break;
			case UserType.LC:
			case UserType.LCP:
				output = UserTypePM.LC;
				break;
			case UserType.ANON:
				output = UserTypePM.ANON;
				break;
			case UserType.MVPD_AUTHZ:
				output = UserTypePM.MVPD_AUTHZ;
				break;
		}
		return output;
	}

	/**
	 * @description For performance logging purposes, returns whether asset is VOD, Livestream or Live Syncbak stream
	 * @return {AssetTypePM}
	 */
	#assetType(): AssetTypePM {
		if ( this.#isLive( this.#tag?.params?.CONTENT ) === false ) {
			return AssetTypePM.VOD;
		} else if ( this.#isSyncbak( this.#tag?.params?.CONTENT ) === true ) {
			return AssetTypePM.SYNCBAK;
		} else {
			return AssetTypePM.LIVE;
		}
	}

	/**
	 * @description For performance logging purposes, returns whether application is SPA
	 * @return {boolean}
	 */
	#isSpa(): boolean {
		if ( !this.utils.Type.isNullOrUndefined( this.#tag?.params?.CONTEXT?.metadata?.isSPA ) ) {
			return this.#tag?.params?.CONTEXT?.metadata?.isSPA;
		} else {
			return false;
		}
	}

	/**
	 * @description Returns true when autoplay is enabled via params AND the browser allows it
	 * (either unmuted or muted). Uses detection results directly rather than the user's
	 * settings.mute preference — now that the settings.mute gate is removed from
	 * SkinController, the prior mute-based branch would report inaccurate metrics.
	 * @returns {boolean}
	 */
	#isAutoplay(): boolean {
		if ( this.#tag?.params?.CONTENT?.autoplay === false ) {
			return false;
		} else if ( this.#isHTML5() === false ) {
			return window?.SmartTag?.autoplay?.supportsUnmutedAutoplay === true
				|| window?.SmartTag?.autoplay?.supportsMutedAutoplay === true;
		} else {
			return true;
		}
	}

	#getUserId = () => {
		return this.#tag?.params?.USER?.userId;
	};

	/**
	 * @description Retrieves Conviva session ID from DataDub when available.
	 * Phase 2: Implement actual DataDub API integration once VTG tracking team delivers.
	 * @returns {string | undefined}
	 */
	#getConvivaSessionId = (): string | undefined => {
		// TODO: Phase 2 - Implement DataDub API integration for Conviva session ID retrieval
		return undefined;
	};

	/**
	 * @description Updates convivaSessionId attribute in New Relic when available from DataDub
	 */
	#updateConvivaSessionId = (): void => {
		const convivaSessionId = this.#getConvivaSessionId();
		if ( !this.utils.Type.isNullOrUndefined( convivaSessionId ) ) {
			this.#logger.log( 'updateConvivaSessionId:', convivaSessionId );
			this.#newRelic.setCustomAttribute( 'convivaSessionId', convivaSessionId );
		}
	};

	/**
	 * @description Returns MPX contentId from apiMetadata (preferred) or CONTENT params
	 * @returns {string | undefined}
	 */
	#getContentId = (): string => {
		// Priority 1: contentId from API metadata (first item) - definitive MPX Content ID
		if ( !this.utils.Type.isNullOrUndefined( this.#tag?.params?.CONTENT?.apiMetadata?.[ 0 ]?.contentId ) ) {
			return this.#tag?.params?.CONTENT?.apiMetadata?.[ 0 ]?.contentId;
		}
		// Priority 2: Direct contentId from CONTENT params (if page passes MPX ID)
		if ( !this.utils.Type.isNullOrUndefined( this.#tag?.params?.CONTENT?.contentId ) ) {
			return this.#tag?.params?.CONTENT?.contentId;
		}
		return undefined;
	};

	/**
	 * @description sets tag instance and initializes event handler
	 * @param {ITag} tag
	 */
	setTag( tag: ITag ) {
		// Performance metrics are only reported when the host enables New Relic via the
		// `newRelic` ready-callback flag (e.g. Pluto TV sets newRelic: false). This mirrors the
		// gate in NewRelicManager.initialize(), so the agent and its metrics turn on/off together.
		if ( this.utils.Convert.stringToBoolean( window?.SmartTag?.runtimeVO?.readyCallback?.newRelic ) !== true ) {
			this.#logger.log( 'setTag:', 'New Relic disabled via ready-callback flag — skipping performance metrics' );
			return;
		}
		this.#tag = tag;
		// setup event handler and NewRelic custom attributes only after tag is set
		this.#initializeEventHandler();
		this.#newRelic.attributes = {
			'appName': this.#appName(),
			'appVersion': this.#appVersion(),
			'assetType': this.#assetType(),
			'browser': this.#browser(),
			'browserVersion': this.#browserVersion(),
			'browserVersionFull': this.#browserVersionFull(),
			'contentId': this.#getContentId(),
			'convivaSessionId': null,
			'device': this.#device(),
			'deviceVersion': this.#deviceVersion(),
			'embedType': this.#embedType(),
			'os': this.#os(),
			'osVersion': this.#osVersion(),
			'platform': this.#platform(),
			'userId': this.#getUserId(),
			'userType': this.#userType(),
			'vAvia': this.#aviaVersion(),
			'vHls': this.#hlsVersion(),
			'vShaka': this.#shakaVersion(),
			'vUtag': this.#utagVersion()
		};
	}

	/**
	 * @description Logs custom metrics in New Relic
	 * @param {Metrics} metric
	 * @param {number} time
	 */
	#setNewRelicPerformanceMetric = ( metric: Metrics, time: number ): void => {
		switch ( metric ) {
			case Metrics.importsLoadTime :
			case Metrics.aviaLoadTime :
				if ( this.#isHTML5() === false ) {
					this.#newRelic.addPageAction( metric, time );
				}
				break;
			case Metrics.importToCreateTime :
				if ( this.#isHTML5() === false && this.#isSpa() === false ) {
					this.#newRelic?.addPageAction( metric, time );
				}
				break;
			case Metrics.createToLoadTime :
			case Metrics.loadToAttachResourceTime :
			case Metrics.attachToVideoStartTime :
				this.#newRelic?.addPageAction( metric, time );
				break;
		}
	};

	/**
	 * @description Player event handler
	 * @param {IPlayerAdapterEventVO} event
	 * @param {number} msFromStart
	 */
	#onPlayerEvent = ( event: IPlayerAdapterEventVO, msFromStart: number ): void => {
		// playback started event handler
		const onPlaybackStarted = (): void => {
			Measure.end( Metrics.attachToVideoStartTime );
			const report = Measure?.report( { roundValues: true } );
			this.#logger.log( 'Measurements finished', report );
			// records performance metrics only when playback is started with autoplay set to true
			if ( this.#isAutoplay() === true ) {
				// grabs average value from Metrics util and set the performance metric
				const averages = report?.averages;
				for ( const key in averages ) {
					if ( !this.utils.Type.isNullOrUndefined( averages[ key ] ) ) {
						const time = +( averages[ key ] / 1000 ).toFixed( 2 );
						this.#setNewRelicPerformanceMetric( key as Metrics, time );
					}
				}
			}
		};

		// filter by player event type
		switch ( event?.type ) {
			case PlayerAdapterEvent.PLAYBACK_STARTED:
				this.#logger.log( TagLoggerString.ON_PLAYER_EVENT, event?.type, 'player adapter event:', event?.type, 'msFromStart:', msFromStart );
				onPlaybackStarted();
				this.#updateConvivaSessionId();
				break;
		}
	};

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		// filter by event type
		switch ( event?.type ) {
			case TagEvent.ON_READY:
				this.#newRelic.addAttributes();
				this.#newRelic.addRelease();
				this.#newRelic.setApplicationVersion();
				// unsubscribe from one-off Tag event
				this.#tag?.eventDispatcher?.removeEventListener( event?.type, this.#onTagEvent );
				break;
			case TagEvent.ON_METADATA_READY:
				// Update contentId when apiMetadata becomes available (preferred source)
				this.#updateContentIdFromMetadata();
				// unsubscribe from one-off Tag event
				this.#tag?.eventDispatcher?.removeEventListener( event?.type, this.#onTagEvent );
				break;
			case TagEvent.ON_PLAYER_EVENT:
				this.#onPlayerEvent( event?.data?.playerAdapterEvent, event?.msFromStart );
				break;
		}
	};

	/**
	 * @description Updates contentId attribute in New Relic when apiMetadata becomes available
	 */
	#updateContentIdFromMetadata = (): void => {
		const contentId = this.#getContentId();
		if ( !this.utils.Type.isNullOrUndefined( contentId ) ) {
			this.#logger.log( 'updateContentIdFromMetadata:', 'Updating contentId in New Relic:', contentId );
			this.#newRelic.setCustomAttribute( 'contentId', contentId );
		}
	};

	#initializeEventHandler() {
		// setup tag events handler
		if ( !this.utils.Type.isNullOrUndefined( this.#tag ) ) {
			this.#tag?.tagEventsHandler(
				this.#onTagEvent,
				[
					TagEvent.ON_READY,
					TagEvent.ON_METADATA_READY,
					TagEvent.ON_PLAYER_EVENT
				],
				this
			);
		}
	}
}